import { periodicAbilityIdForEffect } from '../../../shared/combat/periodicAbilityStatus';

/** Bounded presentation substitutions; incoming status identity still owns teardown. */
export const STATUS_PRESENTATIONS = {
  curse_of_elements: { effectId: 'curse_of_elements_state', incomingEffectIds: ['curse_of_elements'] },
  corruption: { effectId: 'corruption_state', incomingEffectIds: ['corruption'] },
  immolate: { effectId: 'immolate_state', incomingEffectIds: ['immolate'] },
  curse_of_agony: { effectId: 'agony_state', incomingEffectIds: ['agony'] },
  avatar: { effectId: 'avatar_state', incomingEffectIds: ['avatar', 'avatar_aura'] },
  avenging_wrath: { effectId: 'avenging_wrath_state', incomingEffectIds: ['avenging_wrath', 'avenging_wrath_aura', 'wings_of_light'] },
  windfury_totem: { effectId: 'windfury_totem_state', incomingEffectIds: ['windfury_totem', 'windfury_totem_aura'] },
  // Plan 29 F9 (lane CB): the druid cast ids map to the states too, so the
  // offline (no server) path draws the state rather than a one-shot cast.
  barkskin: {
    effectId: 'barkskin_state',
    incomingEffectIds: ['barkskin', 'barkskin_aura', 'barkskin_form'],
  },
  mark_of_the_wild: {
    effectId: 'mark_of_the_wild_state',
    incomingEffectIds: ['mark_of_the_wild', 'mark_of_the_wild_aura', 'defensive_aura', 'wild_mark_cast'],
  },
  // Plan 29 F9 (lane CB): the server's 4 s root on the struck target (it drew
  // the generic `${id}_aura` pulse) and Rejuvenation's heal-over-time.
  entangling_roots: {
    effectId: 'entangling_roots_state',
    incomingEffectIds: ['entangling_roots', 'entangling_roots_aura', 'roots_gather'],
  },
  rejuvenation: {
    effectId: 'rejuvenation_state',
    incomingEffectIds: ['rejuvenation', 'rejuvenation_aura', 'rejuvenation_bloom', 'rejuvenation_state'],
  },
  demon_armor: {
    effectId: 'demon_armor_state',
    incomingEffectIds: ['demon_armor', 'demon_armor_aura', 'defensive_aura'],
  },
  combustion: {
    effectId: 'combustion_state',
    incomingEffectIds: ['combustion', 'combustion_aura', 'combustion_state'],
  },
  // Plan 29 (lane BS): lane BR's server statuses arrive as the ability id and
  // used to resolve to the ability's cast burst — a moment's flash for a
  // 5–12 s state. DefensiveStateVFX.ts draws a persistent state instead.
  shield_wall: {
    effectId: 'shield_wall_state',
    incomingEffectIds: ['shield_wall', 'shield_wall_aura', 'defensive_aura'],
  },
  deterrence: {
    effectId: 'deterrence_state',
    incomingEffectIds: ['deterrence', 'deterrence_aura', 'defensive_aura'],
  },
  cloak_of_shadows: {
    effectId: 'cloak_of_shadows_state',
    incomingEffectIds: ['cloak_of_shadows', 'cloak_of_shadows_aura', 'defensive_aura'],
  },
  // Plan 29 F9 (lane BX): the paladin's two server statuses get their own
  // states (PaladinKitVFX.ts) instead of a generic aura or Divine Shield's
  // one-shot cast bubble.
  divine_shield: {
    effectId: 'divine_shield_state',
    incomingEffectIds: ['divine_shield', 'divine_shield_aura', 'divine_shield_bubble'],
  },
  shield_of_the_righteous: {
    effectId: 'righteous_shield_state',
    incomingEffectIds: ['shield_of_the_righteous', 'shield_of_the_righteous_aura'],
  },
  // Plan 29 F9 (lane BZ): Ice Barrier's 8 s absorb holds a faceted ice shell
  // (MageKitVFX.ts) instead of the old rotating frost_shield aura.
  ice_barrier: {
    effectId: 'ice_barrier_state',
    incomingEffectIds: ['ice_barrier', 'ice_barrier_aura', 'frost_shield', 'ice_barrier_state'],
  },
  howl_of_terror: {
    effectId: 'howl_of_terror_state',
    incomingEffectIds: ['howl_of_terror', 'howl_of_terror_aura'],
  },
  // Plan 29 F9 (lane CA): the priest's server statuses draw states of their
  // own (PriestKitVFX.ts) on whoever holds them: Word of Shielding's absorb
  // (it was the generic `${id}_aura` pulse) and Renew's heal-over-time (the
  // server status now names its ability; it drew nothing on a healed friend).
  word_of_shielding: {
    effectId: 'word_of_shielding_state',
    incomingEffectIds: ['word_of_shielding', 'word_of_shielding_aura', 'word_of_shielding_cast'],
  },
  renew: {
    effectId: 'renew_state',
    incomingEffectIds: ['renew', 'renew_aura', 'renew_cast'],
  },
} as const;

/** Fail closed for unrelated raw statuses, unknown abilities and missing definitions. */
export function resolveStatusPresentation(
  data: { effectId: string; abilityId?: string; data?: Record<string, unknown> | null },
  isRegistered: (effectId: string) => boolean,
): string | undefined {
  if (!data.abilityId || !Object.prototype.hasOwnProperty.call(STATUS_PRESENTATIONS, data.abilityId)) {
    return undefined;
  }
  if (data.abilityId === 'curse_of_elements') {
    const marker = data.data?.abilityStatus as { kinds?: unknown } | undefined;
    if (data.effectId !== 'curse_of_elements' || data.data?.abilityId !== 'curse_of_elements'
      || !Array.isArray(marker?.kinds) || !marker.kinds.includes('incoming_damage')) return undefined;
  }
  const periodicAbilityId = periodicAbilityIdForEffect(data.effectId);
  if (periodicAbilityId) {
    const marker = data.data?.abilityStatus as { kinds?: unknown } | undefined;
    // Seed of Corruption and Siphon Life share the legacy raw corruption ID.
    // Only the canonical periodic contract receives these three new marks.
    if (data.abilityId !== periodicAbilityId || data.data?.abilityId !== periodicAbilityId
      || !Array.isArray(marker?.kinds) || !marker.kinds.includes('periodic_damage')) return undefined;
  }
  const presentation = STATUS_PRESENTATIONS[data.abilityId as keyof typeof STATUS_PRESENTATIONS];
  const incoming: readonly string[] = presentation.incomingEffectIds;
  return incoming.includes(data.effectId) && isRegistered(presentation.effectId)
    ? presentation.effectId
    : undefined;
}

// Coordinates are local to entityVfxAnchor (body center), never hitbox top-left.
// Static shapes retain their read with reduced motion; lifetime comes from the
// existing status event and persistent-effect player, not a new authored timer.
export const BARKSKIN_STATE_STYLE = {
  // Player art is 72px wide at depth100; these narrow edge shapes sit just
  // above it while remaining below the nameplate/health overlay layers.
  depth: 101,
  plateAlpha: 0.78,
  edgeAlpha: 0.96,
  edgeWidth: 1.5,
  platePoints: [-3, -10, 2, -8, 4, -3, 3, 8, -1, 10, -4, 5, -3, 1],
  grainPoints: [{ x: 0, y: -7 }, { x: 1, y: -2 }, { x: -1, y: 3 }, { x: 0, y: 7 }],
  plates: [
    { x: -29, y: -12 }, { x: 29, y: -12 },
    { x: -28, y: 12 }, { x: 28, y: 12 },
  ],
};

export const WILD_MARK_STATE_STYLE = {
  depth: 101,
  leafAlpha: 0.9,
  stemAlpha: 0.94,
  stemWidth: 1.5,
  // A three-leaf side sprig avoids both the face and the overhead name row.
  tipLeafPoints: [0, -8, 4, -2, 3, 3, 0, 7, -3, 3, -4, -2],
  sideLeafPoints: [-7, -7, 0, -5, 5, 1, 6, 6, -1, 4, -5, -1],
  tipX: 36,
  tipY: -11,
  leaves: [{ x: 30, y: 7, mirror: 1 }, { x: 42, y: 7, mirror: -1 }],
  stemPaths: [
    [{ x: 36, y: -7 }, { x: 36, y: 11 }, { x: 34, y: 21 }],
    [{ x: 27, y: 4 }, { x: 36, y: 14 }, { x: 45, y: 4 }],
  ],
};

export const DEMON_ARMOR_STATE_STYLE = {
  depth: 101,
  plateAlpha: 0.78,
  outlineAlpha: 0.96,
  outlineWidth: 2,
  accentWidth: 1.5,
  shoulderX: 28,
  shoulderY: -13,
  shoulderPoints: [-7, 0, -2, -11, 5, -5, 6, 5, 1, 8, 0, 0],
  // Separate angular side rails leave face, torso and feet unobscured.
  outlinePoints: [
    { x: -21, y: -13 }, { x: -26, y: -24 }, { x: -33, y: -18 },
    { x: -34, y: -8 }, { x: -29, y: -5 }, { x: -30, y: 19 }, { x: -23, y: 29 },
  ],
  accentPoints: [{ x: -32, y: -17 }, { x: -27, y: -14 }, { x: -28, y: -8 }],
};

/** Two held hand flames: readable without motion, clear of face and nameplate. */
export const COMBUSTION_STATE_STYLE = {
  depth: 101,
  handX: 29,
  handY: 5,
  flamePoints: [-7, 7, -9, -1, -4, -8, -2, -19, 4, -11, 8, -3, 6, 7, 0, 11],
  corePoints: [-3, 7, -4, 1, 0, -8, 4, 1, 3, 7, 0, 9],
  flameAlpha: 0.8,
  coreAlpha: 0.96,
  outlineWidth: 1.5,
};
